import clsx from 'clsx';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Info, Loader2 } from 'lucide-react';

// ============================================================
// Button
// ============================================================

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  loading?: boolean;
  icon?: ReactNode;
};

const buttonVariants: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 disabled:bg-brand-300',
  secondary:
    'bg-surface text-ink border border-line-strong hover:bg-canvas disabled:text-ink-mute',
  ghost: 'text-ink-soft hover:bg-canvas hover:text-ink',
  danger: 'bg-danger text-white hover:brightness-110 disabled:opacity-50',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition',
        'disabled:cursor-not-allowed',
        size === 'sm' ? 'px-3 py-1.5 text-[13px]' : 'px-4 py-2.5 text-sm',
        buttonVariants[variant],
        className,
      )}
    >
      {loading ? <Loader2 className="size-4 animate-spin" /> : icon}
      {children}
    </button>
  );
}

// ============================================================
// Field (label + input + შეცდომა)
// ============================================================

type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string;
  hint?: string;
};

export function Field({ label, error, hint, className, id, ...rest }: FieldProps) {
  const inputId = id ?? `f-${label.replace(/\s+/g, '-')}`;
  return (
    <div className="space-y-1.5">
      <label htmlFor={inputId} className="block text-[13px] font-medium text-ink-soft">
        {label}
      </label>
      <input
        {...rest}
        id={inputId}
        aria-invalid={Boolean(error)}
        className={clsx(
          'w-full rounded-lg border bg-surface px-3 py-2.5 text-sm text-ink transition',
          'placeholder:text-ink-mute',
          'focus:outline-none focus:ring-3',
          error
            ? 'border-danger focus:border-danger focus:ring-danger-soft'
            : 'border-line-strong focus:border-brand-600 focus:ring-brand-100',
          className,
        )}
      />
      {error ? (
        <p className="text-[12px] text-danger">{error}</p>
      ) : hint ? (
        <p className="text-[12px] text-ink-mute">{hint}</p>
      ) : null}
    </div>
  );
}

// ============================================================
// Card
// ============================================================

export function Card({
  title,
  action,
  className,
  children,
}: {
  title?: ReactNode;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      className={clsx('rounded-[10px] border border-line bg-surface shadow-xs', className)}
    >
      {(title || action) && (
        <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-3.5">
          <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
          {action}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

// ============================================================
// Alert
// ============================================================

const alertStyles = {
  error: { cls: 'bg-danger-soft text-danger border-danger/20', Icon: AlertCircle },
  success: { cls: 'bg-ok-soft text-ok border-ok/20', Icon: CheckCircle2 },
  info: { cls: 'bg-info-soft text-info border-info/20', Icon: Info },
} as const;

export function Alert({
  kind = 'info',
  children,
}: {
  kind?: keyof typeof alertStyles;
  children: ReactNode;
}) {
  const { cls, Icon } = alertStyles[kind];
  return (
    <div className={clsx('flex items-start gap-2.5 rounded-lg border px-3.5 py-3 text-[13px]', cls)}>
      <Icon className="mt-0.5 size-4 shrink-0" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

// ============================================================
// Spinner / სრული ეკრანის ჩატვირთვა
// ============================================================

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={clsx('size-5 animate-spin text-brand-600', className)} />;
}

export function FullPageLoader({ label = 'იტვირთება…' }: { label?: string }) {
  return (
    <div className="flex h-full min-h-[60vh] flex-col items-center justify-center gap-3">
      <Spinner className="size-7" />
      <p className="text-sm text-ink-mute">{label}</p>
    </div>
  );
}

// ============================================================
// ლოგო
// ============================================================

export function Logo({ className, showText = true }: { className?: string; showText?: boolean }) {
  return (
    <div className={clsx('flex items-center gap-2.5', className)}>
      <img src="/step-logo.png" alt="STEP IT Academy" className="size-9 object-contain" />
      {showText && (
        <div className="leading-tight">
          <div className="text-[15px] font-semibold tracking-tight text-ink">IT Academy Step</div>
          <div className="text-[10px] font-medium uppercase tracking-wider text-ink-mute">
            LMS
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// გვერდის სათაური
// ============================================================

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-ink">{title}</h1>
        {subtitle && <p className="mt-0.5 text-[13px] text-ink-mute">{subtitle}</p>}
      </div>
      {action}
    </header>
  );
}

// ============================================================
// ცარიელი მდგომარეობა
// ============================================================

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
      {icon && <div className="mb-1 text-ink-mute">{icon}</div>}
      <p className="text-sm font-medium text-ink">{title}</p>
      {description && <p className="max-w-sm text-[13px] text-ink-mute">{description}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}
